import { isEmpty } from 'lodash';
import moment from 'moment';

import type { Event } from '@appTypes/esb';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { timeFormat } from '@modules/shared/constants';

import type { CalendarEvent } from './duck/types';

export const getTimeOptions = () => {
  const beginning = moment('12:00 am', timeFormat);
  const ending = moment('11:45 pm', timeFormat);
  let current = beginning;
  const options = [];

  while (current.isSameOrBefore(ending)) {
    options.push(current.format(timeFormat));
    current = current.add(15, 'minute');
  }
  return options;
};

export enum Demographics {
  YOUTHS = 'YTH',
  PARENTS = 'PNT',
  LEADERS = 'LED',
  YOUTHS_N_PARENTS = 'YP',
  YOUTHS_N_LEADERS = 'YL',
  PARENTS_N_LEADERS = 'PL',
  ALL = 'ALL',
  NA = 'NA',
}

export enum Recipients {
  LEADER = 'leader',
  YOUTH = 'youth',
  PARENT = 'parent',
}

export const getLookUpDemographicValue = ({
  leader,
  youth,
  parent,
}: {
  leader: boolean;
  youth: boolean;
  parent: boolean;
}) => {
  if (youth && parent && leader) return Demographics.ALL;
  if (youth && leader) return Demographics.YOUTHS_N_LEADERS;
  if (youth && parent) return Demographics.YOUTHS_N_PARENTS;
  if (parent && leader) return Demographics.PARENTS_N_LEADERS;
  if (parent) return Demographics.PARENTS;
  if (leader) return Demographics.LEADERS;
  if (youth) return Demographics.YOUTHS;

  return Demographics.NA;
};

export const getFormDemographicValues = (demo: string) => {
  if (demo === Demographics.NA)
    return {
      naAudience: true,
    };
  if (demo === Demographics.ALL)
    return {
      leadersAudience: true,
      youthAudience: true,
      parentsAudience: true,
    };
  if (demo === Demographics.YOUTHS_N_LEADERS)
    return { leadersAudience: true, youthAudience: true };
  if (demo === Demographics.YOUTHS_N_PARENTS)
    return { youthAudience: true, parentsAudience: true };
  if (demo === Demographics.PARENTS_N_LEADERS)
    return { leadersAudience: true, parentsAudience: true };
  if (demo === Demographics.PARENTS)
    return {
      parentsAudience: true,
    };
  if (demo === Demographics.LEADERS)
    return {
      leadersAudience: true,
    };
  if (demo === Demographics.YOUTHS)
    return {
      youthAudience: true,
    };
  return {};
};

enum EventTagAttr {
  PlansReviewedParents = 'plansReviewedParents',
  GrowthPlan = 'growthPlan',
  BudgetIncludesScouts = 'budgetIncludesScouts',
  OutdoorActivity = 'outdoorActivity',
  ParentalInvolvement = 'parentalInvolvement',
  MyScoutingTools = 'myScoutingTools',
  PatrolTraining = 'patrolTraining',
  ServiceProject = 'serviceProject',
  GroupFitness = 'groupFitness',
  BudgetScoutsParticipate = 'budgetScoutsParticipate',
  ParentOrientation = 'parentOrientation',
  ServiceProjectBenefit = 'serviceProjectBenefit',
}

// Confused? read SBL-5786
export enum EventTag {
  'Sub-Unit Meeting' = EventTagAttr.ServiceProject,
  'Unit Meeting' = EventTagAttr.MyScoutingTools,
  'Youth Training' = EventTagAttr.ServiceProjectBenefit,
  'Adult Training' = EventTagAttr.ParentOrientation,
  'Recruiting' = EventTagAttr.BudgetScoutsParticipate,
  'Camping' = EventTagAttr.OutdoorActivity,
  'Annual Planning' = EventTagAttr.GrowthPlan,
  'Youth Leaders Meeting' = EventTagAttr.PatrolTraining,
  'Recognition Event' = EventTagAttr.BudgetIncludesScouts,
  'Other' = EventTagAttr.PlansReviewedParents,
  'Committee/Parent Meeting' = EventTagAttr.ParentalInvolvement,
  'Long Term Outdoor Event' = EventTagAttr.GroupFitness,
}

export const getTags = (eventTypesName: string[], eventData: Event) => {
  if (!featureFlags.getFlag('SBL_5786_SHOW_TAGS')) return {};
  const resultTags: Record<string, boolean> = {};

  if (eventTypesName?.length) {
    eventTypesName.forEach(attributeKey => (resultTags[attributeKey] = true));
  }

  // New event
  if (isEmpty(eventData)) return resultTags;

  // Editing: Remove event types deselected
  Object.values(EventTagAttr).forEach(attr => {
    const eventHasAttribute = !!eventData[attr];
    const isAttrSelected = eventTypesName.includes(attr);

    if (eventHasAttribute && !isAttrSelected) resultTags[attr] = false;
  });

  return resultTags;
};

export const getFormEventTagsValues = (eventData: Event) => {
  if (!featureFlags.getFlag('SBL_5786_SHOW_TAGS')) return {};
  const result: string[] = [];
  Object.values(EventTagAttr).forEach(attr => {
    const eventHasAttribute = !!eventData[attr];
    if (eventHasAttribute) result.push(attr);
  });
  return { eventTypeTag: result };
};

export const getEventTagsLabels = (
  eventData: Event | CalendarEvent['resource'],
) => {
  if (!eventData || isEmpty(eventData)) return '';

  const resultTags: string[] = [];

  Object.keys(EventTag).forEach(key => {
    const attribute = EventTag[key as keyof typeof EventTag];
    if (eventData[attribute]) resultTags.push(key);
  });

  return resultTags.length > 1
    ? resultTags.toString().replaceAll(',', ', ')
    : resultTags.toString();
};
